'use client';
import classNames from 'classnames';
import { type ComponentPropsWithoutRef, type ElementType, forwardRef, memo, type ReactNode } from 'react';

import { button, buttonSpinner } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const iconButton = tailwindVariants({
  base: 'px-0',
  extend: button,
  variants: {
    size: {
      'extra-large': 'h-12 w-12 px-0 text-16',
      'extra-small': 'h-4 w-4 px-0 text-12',
      large: 'h-10 w-10 px-0 text-16',
      medium: 'h-8 w-8 px-0 text-14',
      small: 'h-6 w-6 px-0 text-12',
    },
  },
});

export const iconButtonSpinner = tailwindVariants({
  extend: buttonSpinner,
});

export const iconButtonIcon = tailwindVariants({
  base: 'block h-4 w-4',
  variants: {
    size: {
      'extra-large': 'h-6 w-6',
      'extra-small': 'h-3 w-3',
      large: 'h-5 w-5',
      medium: 'h-4 w-4',
      small: 'h-4 w-4',
    },
  },
});

export const ICON_BUTTON_ELEMENT_TAG = 'button';

export type IconButtonVariants = VariantProps<typeof iconButton>;

export type IconButtonProps = Omit<
  ComponentPropsWithoutRef<typeof ICON_BUTTON_ELEMENT_TAG>,
  'children' | keyof IconButtonVariants
> &
  IconButtonVariants & {
    isLoading?: boolean;
    label: string;
  } & (
    | {
        icon: ElementType;
      }
    | {
        children: ReactNode;
      }
  );

export const IconButton = memo(
  forwardRef<HTMLButtonElement, IconButtonProps>(
    (
      {
        appearance = 'filled',
        className,
        color = 'blue',
        isLoading,
        label,
        size = 'medium',
        type = 'button',
        ...restOfProps
      },
      forwardedRef,
    ) => {
      const childrenNode =
        'children' in restOfProps ? restOfProps.children : <restOfProps.icon className={iconButtonIcon({ size })} />;

      return (
        <button
          className={classNames(
            iconButton({ appearance, class: className, color, size }),
            isLoading ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
          ref={forwardedRef}
          type={type}
          {...restOfProps}
        >
          {isLoading ? (
            // biome-ignore lint/a11y/useSemanticElements: <This is to indicate a loading state, we pair it w/ aria-live and role="status">
            <div aria-live="polite" role="status">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <Spinner className={iconButtonSpinner({ size })} />
              </div>
              <VisuallyHidden>Loading...</VisuallyHidden>
              <div aria-hidden className="invisible opacity-0">
                {childrenNode}
              </div>
            </div>
          ) : (
            <>
              {label ? <VisuallyHidden>{label}</VisuallyHidden> : label}
              {childrenNode}
            </>
          )}
        </button>
      );
    },
  ),
);

IconButton.displayName = 'IconButton';
